import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import useInviteUser from '@hooks/api/useInviteUser';
import useRetryCreateUser from '@hooks/api/useRetryCreateUser';
import { DashboardSelectorSubmoduleItem } from '../types/dashboardSelector';
import { buildSubmodulePayload } from '../utils/utils';
import { SaveStatus, UserFormRow } from '../types/userDetailsForm';
import { CUSTOM_TEMPLATE } from '../constants';

const toSaveStatus = (index: number, res: any): SaveStatus => ({
  index,
  success: Boolean(res?.code),
  retryId: !Boolean(res?.code) ? (res?.data?.id ?? '') : '',
});

interface InviteParams {
  rows: UserFormRow[];
  companyId: string;
  internalUserAccessTemplateType: string;
  selectedSubmodules: DashboardSelectorSubmoduleItem[];
}

interface RetryParams {
  index: number;
  retryId: string;
  row: UserFormRow;
  companyId: string;
  internalUserAccessTemplateType: string;
  selectedSubmodules: DashboardSelectorSubmoduleItem[];
}

type UseInviteUsersResult = {
  saveStatuses: SaveStatus[];
  isSaving: boolean;
  hasSaveAttempted: boolean;
  handleSave: (params: InviteParams) => Promise<void>;
  handleRetry: (params: RetryParams) => Promise<void>;
};

export const useInviteUsers = (): UseInviteUsersResult => {
  const dispatch = useDispatch();
  const { createUser } = useInviteUser();
  const { reCreateUser } = useRetryCreateUser();

  const [saveStatuses, setSaveStatuses] = useState<SaveStatus[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const inviteOne = async (
    row: UserFormRow,
    companyId: string,
    internalUserAccessTemplateType: string,
    selectedSubmodules: DashboardSelectorSubmoduleItem[],
  ) => {
    const isAdmin = row.userRole === 'ADMIN';
    const res = await createUser({
      email: row.email,
      username: row.username,
      userRole: row.userRole,
      companyId,
      submoduleList: isAdmin ? [] : buildSubmodulePayload(selectedSubmodules),
      ...(!isAdmin &&
        internalUserAccessTemplateType !== CUSTOM_TEMPLATE.internalUserAccessTemplateType && {
          internalUserAccessTemplateType,
        }),
    });

    const success = Boolean((res as any)?.code);
    dispatch(
      setToastMessage({
        type: success ? TOAST_TYPES.SUCCESS : TOAST_TYPES.ERROR,
        message: success
          ? `${row.email} added!`
          : ((res as any)?.error?.message ?? `Failed to add ${row.email}`),
        heading: success ? '' : `Failed to add ${row.email}, please try again !`,
      }),
    );

    return res;
  };

  const handleSave = async ({
    rows,
    companyId,
    internalUserAccessTemplateType,
    selectedSubmodules,
  }: InviteParams) => {
    setIsSaving(true);
    setSaveStatuses([]);

    for (let i = 0; i < rows.length; i++) {
      const res = await inviteOne(
        rows[i],
        companyId,
        internalUserAccessTemplateType,
        selectedSubmodules,
      );
      setSaveStatuses((prev) => [...prev, toSaveStatus(i, res)]);
    }

    setIsSaving(false);
  };

  const handleRetry = async ({
    index,
    retryId,
    row,
    companyId,
    internalUserAccessTemplateType,
    selectedSubmodules,
  }: RetryParams) => {
    setIsSaving(true);

    const res: any = retryId
      ? await reCreateUser({ id: retryId })
      : await inviteOne(row, companyId, internalUserAccessTemplateType, selectedSubmodules);

    setSaveStatuses((prev) => prev.map((s) => (s.index === index ? toSaveStatus(index, res) : s)));
    setIsSaving(false);
  };

  return {
    saveStatuses,
    isSaving,
    hasSaveAttempted: saveStatuses.length > 0,
    handleSave,
    handleRetry,
  };
};
